import * as THREE from 'three';

export const HDRI_BG_SPHERE_NAME = 'hdriBackgroundSphere';

const SPHERE_RADIUS = 1000;
const DEFAULT_HDRI_OFF_COLOR = 0x2d2e32;

/**
 * Sphere must stay inside camera.far (GPU clips beyond far) and still cover
 * the orthographic view rectangle — otherwise ortho preview shows a black sky.
 */
export function getHdriSphereRadiusForCamera(camera) {
  if (!camera) return SPHERE_RADIUS;

  const far = Number(camera.far);
  const safeFar =
    Number.isFinite(far) && far > 0 ? far : SPHERE_RADIUS;

  if (camera.isOrthographicCamera) {
    const zoom = Math.max(Number(camera.zoom) || 1, 0.0001);
    const hw =
      Math.max(Math.abs(camera.left || 0), Math.abs(camera.right || 0)) / zoom;
    const hh =
      Math.max(Math.abs(camera.top || 0), Math.abs(camera.bottom || 0)) / zoom;
    const cover = Math.sqrt(hw * hw + hh * hh);
    // Must cover lateral frustum but stay inside the far plane.
    const minRadius = Math.max(cover * 1.02, 1);
    const maxRadius = Math.max(safeFar * 0.98, 1);
    return Math.min(Math.max(minRadius, 1), maxRadius);
  }

  return Math.min(SPHERE_RADIUS, Math.max(safeFar * 0.9, 1));
}

/**
 * Ensure ortho far is large enough for an inside-out HDRI sphere to fill the view.
 * Returns the far value to use on the viewport camera (does not mutate the asset).
 */
export function getOrthoFarForHdriBackground(camera) {
  if (!camera?.isOrthographicCamera) {
    return camera?.far;
  }
  const zoom = Math.max(Number(camera.zoom) || 1, 0.0001);
  const hw =
    Math.max(Math.abs(camera.left || 0), Math.abs(camera.right || 0)) / zoom;
  const hh =
    Math.max(Math.abs(camera.top || 0), Math.abs(camera.bottom || 0)) / zoom;
  const cover = Math.sqrt(hw * hw + hh * hh);
  const authoredFar = Number(camera.far);
  const safeAuthored =
    Number.isFinite(authoredFar) && authoredFar > 0 ? authoredFar : cover;
  // far must be >= cover so a sphere with radius < far can still span the view.
  return Math.max(safeAuthored, cover * 1.15, 1);
}

export function isHdriSkyBackground(background, envMap, scene) {
  if (!background?.isTexture) return false;
  if (background === envMap) return true;
  if (background === scene?.userData?.__hdriFlatBackgroundTexture) return true;
  if (background === scene?.userData?.__hdriStoredEnvMap) return true;
  if (background.mapping === THREE.EquirectangularReflectionMapping) {
    return true;
  }
  return false;
}

export function getDefaultHdriOffBackground() {
  return new THREE.Color(DEFAULT_HDRI_OFF_COLOR);
}

/** Remember sky before first HDRI apply so visibility-off can restore it. */
export function capturePreHdriBackground(scene, envMap) {
  if (!scene?.userData || scene.userData.__preHdriBackgroundSaved) return;

  const bg = scene.background;
  if (bg && !isHdriSkyBackground(bg, envMap, scene)) {
    scene.userData.__preHdriBackground = bg;
  } else {
    scene.userData.__preHdriBackground = getDefaultHdriOffBackground();
  }
  scene.userData.__preHdriBackgroundSaved = true;
}

export function getHdriOffFallbackBackground(scene, envMap, originalBackgroundRef) {
  const pre = scene?.userData?.__preHdriBackground;
  if (pre && !isHdriSkyBackground(pre, envMap, scene)) {
    return pre;
  }
  if (
    originalBackgroundRef &&
    !isHdriSkyBackground(originalBackgroundRef, envMap, scene)
  ) {
    return originalBackgroundRef;
  }
  return getDefaultHdriOffBackground();
}

export function resetPreHdriBackgroundCapture(scene) {
  if (scene?.userData) {
    delete scene.userData.__preHdriBackground;
    delete scene.userData.__preHdriBackgroundSaved;
    delete scene.userData.__hdriStoredEnvMap;
  }
}

export function hdriRotationToRadians(rotationDegrees) {
  return ((Number(rotationDegrees) || 0) * Math.PI) / 180;
}

export function applyHdriEnvironmentRotation(scene, rotationDegrees) {
  if (!scene?.environmentRotation) return;
  scene.environmentRotation.y = hdriRotationToRadians(rotationDegrees);
}

export function applyHdriBackgroundRotation(scene, rotationDegrees) {
  if (!scene?.backgroundRotation) return;
  scene.backgroundRotation.y = hdriRotationToRadians(rotationDegrees);
}

export function applyHdriSphereRotation(sphere, rotationDegrees) {
  if (!sphere) return;
  sphere.rotation.y = hdriRotationToRadians(rotationDegrees);
}

export function normalizeHdriLightIntensity(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 1;
  return Math.max(0, Math.min(5, n));
}

/** HDRI image brightness (background) + IBL strength on materials (environment). */
export function applyHdriLightIntensity(scene, lightIntensity = 1) {
  if (!scene) return;

  const intensity = normalizeHdriLightIntensity(lightIntensity);
  scene.userData.__hdriLightIntensity = intensity;

  if (scene.userData.__hdriVisible === false) return;

  if (typeof scene.environmentIntensity === 'number') {
    scene.environmentIntensity = intensity;
  }

  if (
    typeof scene.backgroundIntensity === 'number' &&
    scene.background?.isTexture
  ) {
    scene.backgroundIntensity = intensity;
  }

  const sphere = scene.getObjectByName(HDRI_BG_SPHERE_NAME);
  if (sphere?.material?.color) {
    sphere.material.color.setScalar(intensity);
    sphere.material.needsUpdate = true;
  }
}

/** Perspective: native sky. Orthographic: sphere (avoids internal cube background). */
export function shouldUseHdriBackgroundSphere(camera) {
  return camera?.isOrthographicCamera === true;
}

/** Active viewport camera from GridPage (perspective or orthographic). */
export function getActiveViewportCamera() {
  if (typeof window === 'undefined') return null;
  return window.currentCamera ?? window.camera ?? null;
}

export function disposeFlatHdriBackgroundTexture(scene) {
  if (!scene?.userData) return;
  const prev = scene.userData.__hdriFlatBackgroundTexture;
  if (prev?.isTexture) {
    prev.dispose();
    delete scene.userData.__hdriFlatBackgroundTexture;
  }
  delete scene.userData.__hdriFlatBackgroundSourceUuid;
}

function disposeSphereMesh(sphere) {
  if (!sphere) return;
  sphere.geometry?.dispose?.();
  const mats = Array.isArray(sphere.material)
    ? sphere.material
    : [sphere.material];
  mats.forEach((m) => m?.dispose?.());
}

export function removeHdriBackgroundSphere(scene) {
  if (!scene) return;

  const removeNamed = (root) => {
    const existing = root.getObjectByName?.(HDRI_BG_SPHERE_NAME);
    if (existing) {
      disposeSphereMesh(existing);
      root.remove(existing);
    }
  };

  removeNamed(scene);
  scene.traverse((obj) => {
    if (obj.isCamera) removeNamed(obj);
  });
}

function configureHdriSphereMesh(sphere, envMap, rotationDegrees, camera = null) {
  sphere.name = HDRI_BG_SPHERE_NAME;
  sphere.renderOrder = -999;
  sphere.frustumCulled = false;
  sphere.userData.isHdriBackground = true;
  sphere.userData.selectable = false;
  sphere.raycast = () => {};

  if (sphere.material?.map !== envMap) {
    sphere.material.map = envMap;
    sphere.material.needsUpdate = true;
  }

  const radius = getHdriSphereRadiusForCamera(camera);
  const scale = radius / SPHERE_RADIUS;
  sphere.scale.set(scale, scale, scale);

  applyHdriSphereRotation(sphere, rotationDegrees);
}

/**
 * Sky dome for orthographic (and fallback). BackSide from inside — no scale(-1) flip
 * (that + FrontSide caused black polygon facets when rotating).
 */
export function ensureHdriBackgroundSphere(scene, envMap, rotationDegrees, camera = null) {
  if (!scene || !envMap?.isTexture) return null;

  disposeFlatHdriBackgroundTexture(scene);
  scene.background = null;
  if (scene.backgroundRotation) {
    scene.backgroundRotation.set(0, 0, 0);
  }

  let sphere = scene.getObjectByName(HDRI_BG_SPHERE_NAME);

  if (sphere) {
    configureHdriSphereMesh(sphere, envMap, rotationDegrees, camera);
    return sphere;
  }

  const geometry = new THREE.SphereGeometry(SPHERE_RADIUS, 64, 64);

  const material = new THREE.MeshBasicMaterial({
    map: envMap,
    side: THREE.BackSide,
    depthWrite: false,
    depthTest: false,
    fog: false,
    transparent: false,
    toneMapped: false,
  });

  sphere = new THREE.Mesh(geometry, material);
  configureHdriSphereMesh(sphere, envMap, rotationDegrees, camera);

  sphere.onBeforeRender = (_renderer, _scene, renderCamera) => {
    if (renderCamera) {
      sphere.position.copy(renderCamera.position);
      // Keep radius matched to the camera currently drawing the frame.
      const radius = getHdriSphereRadiusForCamera(renderCamera);
      const scale = radius / SPHERE_RADIUS;
      sphere.scale.set(scale, scale, scale);
    }
  };

  scene.add(sphere);
  return sphere;
}

/**
 * Turn off HDRI sky image and IBL — both scene.background and scene.environment.
 */
export function clearHdriEnvironmentAndBackground(
  scene,
  fallbackBackground = null,
  envMap = null
) {
  if (!scene) return;

  removeHdriBackgroundSphere(scene);
  disposeFlatHdriBackgroundTexture(scene);

  scene.environment = null;
  if (scene.environmentRotation) {
    scene.environmentRotation.set(0, 0, 0);
  }
  if (scene.backgroundRotation) {
    scene.backgroundRotation.set(0, 0, 0);
  }
  if (typeof scene.backgroundIntensity === 'number') {
    scene.backgroundIntensity = 1;
  }
  if (typeof scene.environmentIntensity === 'number') {
    scene.environmentIntensity = 1;
  }

  const safeFallback = isHdriSkyBackground(fallbackBackground, envMap, scene)
    ? getDefaultHdriOffBackground()
    : fallbackBackground ?? getDefaultHdriOffBackground();

  scene.background = safeFallback;
}

export function applyHdriToSceneVisual(
  scene,
  envMap,
  rotationDegrees,
  hdriVisible = true,
  camera = null,
  fallbackBackground = null,
  lightIntensity = null
) {
  if (!scene || !envMap?.isTexture) return;

  const resolvedIntensity = normalizeHdriLightIntensity(
    lightIntensity ?? scene.userData?.__hdriLightIntensity ?? 1
  );

  scene.userData.__hdriStoredEnvMap = envMap;
  scene.userData.__hdriRotation = rotationDegrees;
  scene.userData.__hdriVisible = !!hdriVisible;
  scene.userData.__hdriLightIntensity = resolvedIntensity;

  if (!hdriVisible) {
    clearHdriEnvironmentAndBackground(scene, fallbackBackground, envMap);
    return;
  }

  capturePreHdriBackground(scene, envMap);
  envMap.mapping = THREE.EquirectangularReflectionMapping;

  scene.environment = envMap;
  applyHdriEnvironmentRotation(scene, rotationDegrees);

  if (shouldUseHdriBackgroundSphere(camera)) {
    removeHdriBackgroundSphere(scene);
    ensureHdriBackgroundSphere(scene, envMap, rotationDegrees, camera);
  } else {
    removeHdriBackgroundSphere(scene);
    disposeFlatHdriBackgroundTexture(scene);
    scene.background = envMap;
    applyHdriBackgroundRotation(scene, rotationDegrees);
  }

  applyHdriLightIntensity(scene, resolvedIntensity);
}

/**
 * Re-apply HDRI sky (sphere vs scene.background) after camera type changes.
 * Camera switches in GridPage do not re-render React, so EnvironmentComponent
 * may not run updateBackground when toggling orthographic / perspective.
 */
export function syncHdriBackgroundForCamera(scene, camera) {
  if (!scene) return;

  const envMap =
    scene.userData.__hdriStoredEnvMap ||
    (scene.environment?.isTexture ? scene.environment : null);

  if (!envMap?.isTexture) return;
  if (scene.userData.__hdriVisible === false) return;

  const rotationDegrees = scene.userData.__hdriRotation ?? 0;
  const lightIntensity = scene.userData.__hdriLightIntensity ?? 1;
  const fallbackBackground = getHdriOffFallbackBackground(
    scene,
    envMap,
    scene.userData.__preHdriBackground
  );

  const useSphere = shouldUseHdriBackgroundSphere(camera);
  const sphere = scene.getObjectByName(HDRI_BG_SPHERE_NAME);
  const needsFullApply =
    useSphere !== !!sphere ||
    (useSphere && sphere?.material?.map !== envMap) ||
    (!useSphere &&
      (scene.background !== envMap ||
        !isHdriSkyBackground(scene.background, envMap, scene)));

  if (needsFullApply) {
    applyHdriToSceneVisual(
      scene,
      envMap,
      rotationDegrees,
      true,
      camera,
      fallbackBackground,
      lightIntensity
    );
    return;
  }

  scene.environment = envMap;
  applyHdriEnvironmentRotation(scene, rotationDegrees);
  if (useSphere) {
    const existing = scene.getObjectByName(HDRI_BG_SPHERE_NAME);
    if (existing) {
      configureHdriSphereMesh(existing, envMap, rotationDegrees, camera);
    }
  }
  updateHdriRotation(scene, rotationDegrees, camera);
  applyHdriLightIntensity(scene, lightIntensity);
}

export function updateHdriRotation(scene, rotationDegrees, camera = null) {
  if (!scene) return;
  if (scene.userData) {
    scene.userData.__hdriRotation = rotationDegrees;
  }
  applyHdriEnvironmentRotation(scene, rotationDegrees);

  if (shouldUseHdriBackgroundSphere(camera)) {
    const sphere = scene.getObjectByName(HDRI_BG_SPHERE_NAME);
    if (sphere) {
      applyHdriSphereRotation(sphere, rotationDegrees);
    }
  } else {
    applyHdriBackgroundRotation(scene, rotationDegrees);
  }
}

/** @deprecated */
export function setSceneBackgroundFromHdri(scene, envMap) {
  applyHdriToSceneVisual(scene, envMap, 0, true, null);
}
